import { QueryClient } from "@tanstack/react-query";

import { VideoProject, VideoProjectQueueSearchParams, VideoProjectServices } from "@/services/bedrock";

import { getKanbanColumnId, KANBAN_COLUMNS, KanbanColumnDef, KanbanColumnId } from "./kanbanColumns";
import { insertProjectInKanbanQueueOrder } from "./kanbanQueueOrder";

export const KANBAN_COLUMN_PAGE_SIZE = 10;
export const KANBAN_EXTRA_FIELDS = {
  needsAck: true,
  isPriority: true,
  queueAssignments: ["staffUser"],
} as const;

export type KanbanBoardFilters = Pick<
  VideoProjectQueueSearchParams,
  "searchTerm" | "includeDemoProjects" | "assignees"
>;

export interface KanbanColumnSort {
  sort: string;
  order: "asc" | "desc";
}

export interface KanbanColumnFetchResult {
  projects: VideoProject[];
  totalCount: number;
  loadedPageCount: number;
  sort: KanbanColumnSort;
}

export type KanbanBoardData = Record<KanbanColumnId, KanbanColumnFetchResult>;

export function mediaUrlIdentity(url: string | null | undefined): string {
  if (!url) return "";
  try {
    const parsed = new URL(url, "https://kanban-media.local");
    const protocol = parsed.protocol.toLowerCase();
    const hostname = parsed.hostname.toLowerCase();
    const isRelative = parsed.origin === "https://kanban-media.local";
    if (isRelative) return decodeURI(parsed.pathname);

    return `${protocol}//${hostname}${decodeURI(parsed.pathname)}`;
  } catch {
    const [withoutHash] = url.split("#");
    return withoutHash.split("?")[0];
  }
}

function queueAssignmentSignature(project: VideoProject): string {
  return (project.queueAssignments ?? [])
    .map(assignment => `${assignment.role}:${assignment.staffUserId}`)
    .sort()
    .join(",");
}

function kanbanProjectCardKey(project: VideoProject): string {
  return [
    project.id,
    project.status,
    project.statusLabel ?? "",
    project.isPriority ? "1" : "0",
    project.needsAck ? "1" : "0",
    project.editorUserId ?? "",
    project.qaUserId ?? "",
    project.qaStatus ?? "",
    project.playerName ?? "",
    project.companyName ?? "",
    project.revisionCount ?? 0,
    project.requiresQa ? "1" : "0",
    mediaUrlIdentity(project.thumbnailUrl),
    project.createdDate ?? "",
    queueAssignmentSignature(project),
  ].join("|");
}

export function reuseVideoProject(previous: VideoProject | undefined, next: VideoProject): VideoProject {
  if (!previous || previous === next) return previous ?? next;
  if (previous.id !== next.id) return next;
  if (kanbanProjectCardKey(previous) === kanbanProjectCardKey(next)) {
    return previous;
  }
  if (mediaUrlIdentity(previous.thumbnailUrl) === mediaUrlIdentity(next.thumbnailUrl) && previous.thumbnailUrl) {
    return { ...next, thumbnailUrl: previous.thumbnailUrl };
  }
  return next;
}

export function reuseKanbanProjectList(previous: VideoProject[] | undefined, next: VideoProject[]): VideoProject[] {
  if (!previous) return next;
  if (previous === next) return previous;

  const previousById = new Map(previous.map(project => [project.id, project]));
  let sameOrderAndRefs = previous.length === next.length;
  const merged = next.map((project, index) => {
    const reused = reuseVideoProject(previousById.get(project.id), project);
    if (sameOrderAndRefs && previous[index] !== reused) sameOrderAndRefs = false;
    return reused;
  });
  return sameOrderAndRefs ? previous : merged;
}

export function reuseKanbanBoardData(previous: KanbanBoardData | undefined, next: KanbanBoardData): KanbanBoardData {
  if (!previous) return next;

  let changed = false;
  const result = { ...next };
  for (const column of KANBAN_COLUMNS) {
    const previousColumn = previous[column.id];
    const nextColumn = next[column.id];
    if (!nextColumn) {
      if (previousColumn) changed = true;
      continue;
    }
    if (!previousColumn) {
      changed = true;
      continue;
    }

    const projects = reuseKanbanProjectList(previousColumn.projects, nextColumn.projects);
    if (
      projects === previousColumn.projects &&
      previousColumn.totalCount === nextColumn.totalCount &&
      previousColumn.loadedPageCount === nextColumn.loadedPageCount &&
      previousColumn.sort.sort === nextColumn.sort.sort &&
      previousColumn.sort.order === nextColumn.sort.order
    ) {
      result[column.id] = previousColumn;
      continue;
    }

    result[column.id] = projects === nextColumn.projects ? nextColumn : { ...nextColumn, projects };
    changed = true;
  }

  return changed ? result : previous;
}

export function kanbanBoardQueryKey(filters: KanbanBoardFilters) {
  return ["videoProjects", "kanbanBoard", filters] as const;
}

function getColumnSort(column: KanbanColumnDef): KanbanColumnSort {
  return {
    sort: column.sort ?? "queue_priority",
    order: column.order ?? "asc",
  };
}

export function isKanbanColumnFetchAborted(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) return true;
  if (error == null || typeof error !== "object") return false;
  if (error instanceof DOMException && error.name === "AbortError") return true;
  const maybe = error as { name?: string; code?: string; message?: string };
  return (
    maybe.name === "AbortError" ||
    maybe.name === "CanceledError" ||
    maybe.code === "ERR_CANCELED" ||
    maybe.message === "canceled"
  );
}

async function fetchKanbanColumnPage(
  column: KanbanColumnDef,
  filters: KanbanBoardFilters,
  page: number,
  signal?: AbortSignal
): Promise<{ projects: VideoProject[]; totalCount: number }> {
  const sort = getColumnSort(column);
  const response = await VideoProjectServices.index(
    {
      ...filters,
      status: column.statuses,
      perPage: KANBAN_COLUMN_PAGE_SIZE,
      sort: sort.sort,
      order: sort.order,
      page,
      extraFields: KANBAN_EXTRA_FIELDS,
    },
    signal ? { signal } : undefined
  );

  return {
    projects: response.content,
    totalCount: response.pagination.totalCount,
  };
}

async function fetchKanbanColumnPages(
  column: KanbanColumnDef,
  filters: KanbanBoardFilters,
  pageCount: number,
  signal?: AbortSignal
): Promise<KanbanColumnFetchResult> {
  const sort = getColumnSort(column);
  const pageResults = await Promise.all(
    Array.from({ length: pageCount }, (_, index) => fetchKanbanColumnPage(column, filters, index + 1, signal))
  );
  const projects = pageResults.flatMap(result => result.projects);

  return {
    projects,
    totalCount: pageResults[0]?.totalCount ?? 0,
    loadedPageCount: pageCount,
    sort,
  };
}

export function kanbanColumnHasMorePages(columnData: KanbanColumnFetchResult): boolean {
  return columnData.projects.length < columnData.totalCount;
}

export async function fetchNextKanbanColumnPage(
  column: KanbanColumnDef,
  filters: KanbanBoardFilters,
  columnData: KanbanColumnFetchResult,
  signal?: AbortSignal
): Promise<{ projects: VideoProject[]; totalCount: number }> {
  return fetchKanbanColumnPage(column, filters, columnData.loadedPageCount + 1, signal);
}

export async function fetchKanbanBoard(
  filters: KanbanBoardFilters,
  options?: { signal?: AbortSignal }
): Promise<KanbanBoardData> {
  const columnResults = await Promise.all(
    KANBAN_COLUMNS.map(async column => {
      const result = await fetchKanbanColumnPages(column, filters, 1, options?.signal);
      return [column.id, result] as const;
    })
  );

  return Object.fromEntries(columnResults) as KanbanBoardData;
}

export async function refreshKanbanColumns(
  filters: KanbanBoardFilters,
  boardData: KanbanBoardData,
  columnIds: KanbanColumnId[],
  signal?: AbortSignal
): Promise<KanbanBoardData> {
  const uniqueColumnIds = [...new Set(columnIds)];
  const refreshedColumns = await Promise.all(
    uniqueColumnIds.map(async columnId => {
      const column = KANBAN_COLUMNS.find(item => item.id === columnId);
      const columnData = boardData[columnId];
      if (!column || !columnData) return null;

      const pageCount = Math.max(1, columnData.loadedPageCount);
      const refreshed = await fetchKanbanColumnPages(column, filters, pageCount, signal);
      return [columnId, refreshed] as const;
    })
  );

  const updates = Object.fromEntries(
    refreshedColumns.filter((entry): entry is readonly [KanbanColumnId, KanbanColumnFetchResult] => entry != null)
  );

  return reuseKanbanBoardData(boardData, { ...boardData, ...updates });
}

export function appendKanbanColumnPage(
  boardData: KanbanBoardData,
  columnId: KanbanColumnId,
  pageProjects: VideoProject[],
  totalCount: number
): KanbanBoardData {
  const columnData = boardData[columnId];
  if (!columnData) return boardData;

  const existingIds = new Set(columnData.projects.map(project => project.id));
  const newProjects = pageProjects.filter(project => !existingIds.has(project.id));
  if (newProjects.length === 0 && totalCount === columnData.totalCount) {
    return boardData;
  }

  return {
    ...boardData,
    [columnId]: {
      ...columnData,
      projects: [...columnData.projects, ...newProjects],
      totalCount,
      loadedPageCount: columnData.loadedPageCount + 1,
    },
  };
}

export function moveProjectInKanbanBoardData(
  boardData: KanbanBoardData,
  projectId: number,
  fromColumnId: KanbanColumnId,
  toColumnId: KanbanColumnId,
  patch: Partial<VideoProject>,
  currentUserId: number
): KanbanBoardData {
  const fromColumn = boardData[fromColumnId];
  const toColumn = boardData[toColumnId];
  if (!fromColumn || !toColumn) return boardData;

  const project = fromColumn.projects.find(item => item.id === projectId);
  if (!project) return boardData;

  const updatedProject = { ...project, ...patch };

  if (fromColumnId === toColumnId) {
    return {
      ...boardData,
      [fromColumnId]: {
        ...fromColumn,
        projects: insertProjectInKanbanQueueOrder(
          fromColumn.projects.map(item => (item.id === projectId ? updatedProject : item)),
          updatedProject,
          fromColumnId,
          currentUserId
        ),
      },
    };
  }

  const alreadyInDestination = toColumn.projects.some(item => item.id === projectId);

  return {
    ...boardData,
    [fromColumnId]: {
      ...fromColumn,
      projects: fromColumn.projects.filter(item => item.id !== projectId),
      totalCount: Math.max(0, fromColumn.totalCount - 1),
    },
    [toColumnId]: {
      ...toColumn,
      projects: insertProjectInKanbanQueueOrder(toColumn.projects, updatedProject, toColumnId, currentUserId),
      totalCount: alreadyInDestination ? toColumn.totalCount : toColumn.totalCount + 1,
    },
  };
}

export function updateProjectInKanbanBoardData(
  boardData: KanbanBoardData,
  projectId: number,
  patch: Partial<VideoProject>,
  currentUserId: number
): KanbanBoardData {
  for (const column of KANBAN_COLUMNS) {
    const project = boardData[column.id]?.projects.find(item => item.id === projectId);
    if (!project) continue;

    const toColumnId = getKanbanColumnId(patch.status ?? project.status) ?? column.id;
    return moveProjectInKanbanBoardData(boardData, projectId, column.id, toColumnId, patch, currentUserId);
  }

  return boardData;
}

export function setKanbanBoardQueryData(
  queryClient: QueryClient,
  filters: KanbanBoardFilters,
  boardData: KanbanBoardData
): void {
  queryClient.setQueryData(kanbanBoardQueryKey(filters), boardData);
}
